/**
 * `recurringSchedules.*` contract — generic recurring-document schedules
 * (ADR-0024 / ADR-0025, migration 0061).
 *
 * Targets: `invoice` (sales) and `bill` (purchases). A schedule clones its
 * source document into a fresh draft on cadence. The wire keeps the
 * module-agnostic shape — `targetType` + `sourceDocumentId` — so future
 * owners slot in without a new contract.
 */
import { z } from "zod";
import { isoDate, moneyPaise, signedPaise, uuid } from "../_common.js";

export const cadence = z.enum(["monthly", "quarterly", "yearly"]);
export const targetType = z.enum(["invoice", "bill"]);

/** Create a recurrence. Anchor: `monthEnd` OR a fixed `dayOfMonth` (1-28). */
export const createInput = z
  .object({
    targetType: targetType.default("invoice"),
    sourceDocumentId: uuid,
    label: z.string().trim().min(1).max(160),
    cadence,
    monthEnd: z.boolean(),
    dayOfMonth: z.number().int().min(1).max(28).optional(),
    endsOn: isoDate.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.monthEnd && v.dayOfMonth != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dayOfMonth"],
        message: "Leave the day blank when using last-day-of-month.",
      });
    }
    if (!v.monthEnd && v.dayOfMonth == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dayOfMonth"],
        message: "Pick a day of the month, or choose last day of month.",
      });
    }
  });

export const sourceSummary = z.object({
  docNumber: z.string().nullable(),
  /** Customer (invoice) or vendor (bill) name. */
  counterpartyName: z.string(),
  currencyCode: z.string(),
  totalPaise: moneyPaise,
  /** Stringified bigint minor units when the source invoice is foreign. */
  foreignAmountMinor: signedPaise.nullable(),
});

export const scheduleRow = z.object({
  id: uuid,
  label: z.string(),
  targetType,
  sourceDocumentId: uuid,
  cadence,
  monthEnd: z.boolean(),
  dayOfMonth: z.number().nullable(),
  mode: z.enum(["draft", "issue"]),
  status: z.enum(["active", "paused", "cancelled", "completed"]),
  nextRunOn: isoDate,
  lastRunOn: isoDate.nullable(),
  runsCount: z.number(),
  endsOn: isoDate.nullable(),
  createdAt: z.string(),
  source: sourceSummary.nullable(),
});

export const createOutput = scheduleRow;
export const listOutput = z.array(scheduleRow);

export const setStatusInput = z.object({
  scheduleId: uuid,
  action: z.enum(["pause", "resume", "cancel"]),
});
export const setStatusOutput = scheduleRow;

/** Manual "generate now" — fire one schedule immediately (testing + ad-hoc). */
export const runNowInput = z.object({ scheduleId: uuid });
export const runNowOutput = z.object({
  scheduleId: uuid,
  targetType,
  generatedDocumentId: uuid,
  docNumber: z.string().nullable(),
  nextRunOn: isoDate.nullable(),
  status: z.enum(["active", "paused", "cancelled", "completed"]),
});

export type ScheduleRow = z.infer<typeof scheduleRow>;
